import { X } from 'lucide-react';
import { UserData } from './UsersTable';
import { formatDate } from '../utils/formatUtils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { MultiProgress, ProgressSegment } from '@/components/ui/multi-progress';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface UserProfileCardProps {
  user: UserData | null;
  isOpen: boolean;
  onClose: () => void;
}

const UserProfileCard: React.FC<UserProfileCardProps> = ({ user, isOpen, onClose }) => {
  if (!user) return null;

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const getProgressSegments = (progress?: UserData['progress']): ProgressSegment[] => {
    if (!progress || progress.availableCourses === 0) {
      return [{ value: 100, color: '#e5e7eb', label: 'No data' }];
    }

    return [
      { 
        value: (progress.completedCourses / progress.availableCourses) * 100, 
        color: '#10b981', // green
        label: 'Completed'
      },
      { 
        value: (progress.inProgressCourses / progress.availableCourses) * 100, 
        color: '#3b82f6', // blue
        label: 'In Progress'
      },
      { 
        value: (progress.notStartedCourses / progress.availableCourses) * 100, 
        color: '#d1d5db', // gray
        label: 'Not Started'
      }
    ];
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] p-0">
        <Card className="border-0 shadow-none">
          <CardHeader className="relative pb-2">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={user.avatarUrl} alt={user.name} />
                <AvatarFallback className="text-xl">{getInitials(user.name)}</AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-xl">{user.name}</CardTitle>
                <div className="text-sm text-muted-foreground mt-1">{user.email}</div>
                <div className="flex gap-2 mt-2">
                  <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                    {user.role === 'admin' ? 'Admin' : 'Student'}
                  </Badge>
                  <Badge 
                    variant={user.status === 'active' ? 'success' : 'outline'}
                    className={
                      user.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }
                  >
                    {user.status === 'active' ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Organization</h3>
                <p className="mt-1">{user.organization}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Member Since</h3>
                <p className="mt-1">{formatDate(user.createdAt)}</p>
              </div>
              {user.lastActive && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Last Active</h3>
                  <p className="mt-1">{formatDate(user.lastActive)}</p>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Course Progression</h3>
              <MultiProgress 
                segments={getProgressSegments(user.progress)} 
                className="h-3 mb-3"
              />
              
              {user.progress && user.progress.availableCourses > 0 ? (
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-1">
                    <span className="text-gray-600">Completed:</span>
                    <span className="font-medium">{user.progress.completedCourses}</span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-1">
                    <span className="text-gray-600">In Progress:</span>
                    <span className="font-medium">{user.progress.inProgressCourses}</span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-1">
                    <span className="text-gray-600">Not Started:</span>
                    <span className="font-medium">{user.progress.notStartedCourses}</span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-1">
                    <span className="text-gray-600">Total Available:</span>
                    <span className="font-medium">{user.progress.availableCourses}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No course data available</p>
              )}
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button 
              variant="default"
              onClick={() => window.location.href = `mailto:${user.email}`}
            >
              Send Email
            </Button>
          </CardFooter>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileCard; 